import { Component, Element, Prop, h, Event, EventEmitter } from '@stencil/core';

@Component({
  tag: 'spw-button',
  styleUrl: 'spw-button.scss',
  shadow: true,
})
export class SpwButton {
  @Element() el: HTMLSpwButtonElement;

  /** Spécifie le lien si nécessaire */
  @Prop() href?: string;
  /** Attribut target, s'applique uniquement si href est rempli */
  @Prop() target?: string = '_self';
  /** Remplit l'attribut rel */
  @Prop() rel?: string;
  /** Permet de télécharger un fichier au lieu de naviguer vers le lien */
  @Prop() download?: string;
  /** Nom du bouton */
  @Prop() name?: string = '';
  /** Valeur du bouton */
  @Prop() value?: string | number = '';
  /** Type du bouton */
  @Prop() type?: 'button' | 'submit' | 'reset' = 'button';

  /** Permet de sélectionner une variante du bouton */
  @Prop() variant?: 'primary' | 'secondary' | 'tertiary' = 'primary';
  /** Permet de choisir la surface d'arrière-plan du bouton */
  @Prop() surface?: 'default' | 'dark' | 'light' = 'default';
  /** Si désactivé, le bouton n'est pas utilisable */
  @Prop() disabled?: boolean = false;
  /** Permet de spécifier la taille du bouton */
  @Prop() size?: 'large' | 'medium' | 'small' = 'medium';

  /** Remplit l'attribut aria-label, utilisé à des fins d'accessibilité */
  @Prop() accAriaLabel?: string;

  /** Si rempli, vous pouvez utiliser une référence d'icône font-awesome */
  @Prop() icon?: string;
  /** Position de l'icône à l'intérieur du bouton */
  @Prop() iconPosition?: 'left' | 'right';

  /** Si vrai, le bouton prend toute la largeur */
  @Prop() isFullWidth?: boolean = false;
  /** Si vrai, le bouton prend toute la largeur en mobile */
  @Prop({ reflect: true }) isFullWidthMobile?: boolean = true;

  /** Lors du focus */
  @Event() spwFocus: EventEmitter<void>;
  /** Lors de la perte du focus */
  @Event() spwBlur: EventEmitter<void>;
  /** Navigation vers un lien lors du clic */
  @Event() spwNavigate: EventEmitter<MouseEvent>;

  private onFocus = () => {
    this.spwFocus.emit();
  };

  private onBlur = () => {
    this.spwBlur.emit();
  };

  private resetFormInputs() {
    const form = this.el.closest('form');
    if (!form) return;

    const spwInputs = form.querySelectorAll('spw-input');
    spwInputs.forEach(spwInput => {
      const input = spwInput.shadowRoot.querySelector('input');
      if (input) {
        input.value = '';
      }
    });
  }

  private onClick = (ev: MouseEvent) => {
    if (this.disabled) {
      ev.preventDefault();
      return;
    }

    if (this.type === 'reset') {
      ev.preventDefault();
      this.resetFormInputs();
      return;
    }

    if (this.type === 'submit' && !this.disabled) {
      const form = this.el.closest('form');
      if (form) {
        ev.preventDefault();
        form.requestSubmit();
        return;
      }
    }

    if (this.href !== undefined) {
      this.spwNavigate.emit(ev);
    }
  };

  private get elementClass(): { [className: string]: boolean } {
    return {
      'spw-button': true,
      [`spw-button--${this.variant}`]: true,
      [`spw-button--surface-${this.surface}`]: true,
      [`spw-button--${this.size}`]: true,
      [`spw-button--fullwidth`]: this.isFullWidth,
      [`spw-button--fullwidth-mobile`]: this.isFullWidthMobile,
    };
  }

  render() {
    const { type, download, href, rel, target, name, value } = this;
    const TagType = this.href === undefined ? 'button' : 'a';
    const attrs =
      TagType === 'button'
        ? { type: type, name, value }
        : {
            download,
            href,
            rel,
            target,
          };

    const iconPosition = this.icon && !this.iconPosition ? 'right' : this.iconPosition;
    const iconClass = `spw-icon--${iconPosition}`;
    const iconElement = this.icon ? <spw-icon class={iconClass} icon={this.icon}></spw-icon> : null;

    const ariaAttributes = {
      'aria-label': this?.accAriaLabel,
      'aria-disabled': this.disabled ? 'true' : null,
    };

    return (
      <TagType {...attrs} class={this.elementClass} disabled={this.disabled} onFocus={this.onFocus} onBlur={this.onBlur} onClick={this.onClick} {...ariaAttributes}>
        <div class="spw-button__inner">
          {iconPosition === 'left' && iconElement}
          <slot></slot>
          {iconPosition === 'right' && iconElement}
        </div>
      </TagType>
    );
  }
}
